/**
 * SOS (Anf. 11, ARCHITECTURE.md §9).
 *
 * Die Funktion, bei der ein Fehler am teuersten ist. Deshalb steht hier mehr
 * Begründung als Code.
 *
 * ## Der Satz, der überall danebenstehen muss
 * Family Hub ersetzt **keinen Notruf**. Es ist ein Familien-Alarm. Wer wirklich
 * Hilfe braucht, ruft 112. Diese Zusage steht nicht im Kleingedruckten,
 * sondern als Knopf im SOS-Bildschirm — siehe `NOTRUFNUMMERN`.
 */
/**
 * Wie lange der Knopf gehalten werden muss.
 *
 * Anf. 11 verlangt ausdrücklich, dass ein einzelner Tap NICHT auslöst. In einer
 * Hosentasche passiert ein Tap ständig; 1,5 Sekunden Halten passiert nicht
 * versehentlich. Die Alternative — zwei Taps — steht daneben, weil Halten mit
 * zitternden Händen schwerfällt.
 */
export const HALTEN_MS = 1500;
/**
 * Countdown zwischen Auslösen und Alarm.
 *
 * Fünf Sekunden sind der Kompromiss: lang genug, um einen Fehlgriff zu
 * bemerken, kurz genug, dass niemand in einer echten Notlage wartet.
 *
 * Der Countdown läuft **serverseitig**. Das ist der wichtige Teil: Fällt das
 * Telefon in diesen fünf Sekunden aus — Akku leer, ins Wasser gefallen, aus der
 * Hand geschlagen —, geht der Alarm trotzdem raus. Ein Countdown, der nur im
 * Browser läuft, verliert genau die Fälle, für die SOS da ist.
 */
export const COUNTDOWN_SEKUNDEN = 5;
/**
 * Eskalationsstufen (ARCHITECTURE.md §9).
 *
 * Läuft serverseitig und unabhängig vom auslösenden Gerät — ein Alarm, dessen
 * Eskalation am Telefon des Opfers hängt, eskaliert genau dann nicht, wenn es
 * darauf ankommt.
 */
export const ESKALATION = [
    {
        level: 1,
        nachSekunden: 0,
        wen: 'Alle mit SOS-Empfang',
        was: 'Alarm auf allen Geräten, Push auch bei geschlossener App',
    },
    {
        level: 2,
        nachSekunden: 60,
        wen: 'Dieselben, erneut',
        was: 'Wiederholungsalarm mit Ton — auch wenn das Gerät stummgeschaltet ist',
    },
    {
        level: 3,
        nachSekunden: 180,
        wen: 'Alle Erwachsenen der Familie',
        was: 'Zusätzlich der deutliche Hinweis, den Notruf zu wählen',
    },
];
export const MAX_LEVEL = 3;
/** Testalarme eskalieren nicht über Stufe 1 — sonst wäre der Test kein Test. */
export const TEST_MAX_LEVEL = 1;
/**
 * Zwei Alarme derselben Person innerhalb dieser Zeit sind derselbe Alarm.
 *
 * Der Grund ist der Offline-Fall: Der Service Worker wiederholt den Versand,
 * bis er durchkommt. Käme jede Wiederholung als neuer Alarm an, klingelte es
 * bei der Familie fünfmal — und niemand wüsste, ob das fünf Notfälle sind.
 *
 * Deshalb wird zusammengeführt statt abgelehnt: Eine Ablehnung würde der
 * Service Worker als Fehler werten und weiter wiederholen.
 */
export const DUPLIKAT_FENSTER_SEKUNDEN = 60;
/** Wie lange ein unbeantworteter Alarm offen bleibt, bevor er verfällt. */
export const ALARM_MAX_STUNDEN = 12;
/**
 * Notrufnummern je Land.
 *
 * Als `tel:`-Link — mehr geht aus einem Browser nicht. Ein automatischer Anruf
 * ist technisch unmöglich und wäre rechtlich heikel (RISIKEN §8). Der Nutzer
 * tippt einmal, das Telefon wählt.
 *
 * Wichtig: Ein Notruf funktioniert auch dort, wo kein Datenverkehr läuft, und
 * sogar im Fremdnetz. Im Funkloch ist er der zuverlässigere Weg — nicht diese
 * App.
 */
export const NOTRUFNUMMERN = {
    DE: [
        { nummer: '112', label: 'Notruf (Rettung, Feuerwehr)' },
        { nummer: '110', label: 'Polizei' },
    ],
    AT: [
        { nummer: '112', label: 'Euro-Notruf' },
        { nummer: '144', label: 'Rettung' },
    ],
    CH: [
        { nummer: '112', label: 'Euro-Notruf' },
        { nummer: '144', label: 'Sanität' },
    ],
};
export function notrufnummern(countryCode) {
    return NOTRUFNUMMERN[countryCode.toUpperCase()] ?? NOTRUFNUMMERN.DE;
}
/**
 * Der Hinweis, der in der App steht — nicht in einer Datenschutzerklärung.
 *
 * Wer glaubt, mit SOS den Rettungsdienst zu rufen, verliert im Ernstfall
 * Minuten. Diese Zeile ist deshalb Teil der Funktion, nicht ihres Beiwerks.
 */
export const KEIN_NOTRUF_HINWEIS = 'Das hier ist ein Familien-Alarm, kein Notruf. ' +
    'Bei Lebensgefahr wähle die 112 — das geht auch ohne Internet.';
/** Was Empfänger sehen. Testalarme müssen auf den ersten Blick erkennbar sein. */
export const TEST_HINWEIS = '🧪 TEST — keine echte Notlage';
export const STATE_TEXT = {
    armed: 'Countdown läuft',
    active: 'Alarm läuft',
    acknowledged: 'Jemand kümmert sich',
    resolved: 'Erledigt',
    cancelled: 'Abgebrochen',
    false_alarm: 'Fehlalarm',
};
/** Ist der Alarm noch offen — also: kümmert sich noch niemand ausreichend? */
export function istOffen(state) {
    return state === 'armed' || state === 'active';
}
/**
 * Welche Stufe gilt nach dieser Zeit?
 *
 * Reine Funktion, damit Server und Oberfläche dasselbe rechnen — die App zeigt
 * an, wann die nächste Stufe kommt, und das muss stimmen.
 */
export function stufeNach(sekunden, kind = 'real') {
    const max = kind === 'test' ? TEST_MAX_LEVEL : MAX_LEVEL;
    let stufe = 1;
    for (const e of ESKALATION) {
        if (sekunden >= e.nachSekunden)
            stufe = e.level;
    }
    return Math.min(stufe, max);
}
//# sourceMappingURL=sos.js.map